import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import {
  Login,
  PageNotFound,
  Workspace as WorkspaceOverview,
  EmailVerification,
  SettingsOverview,
  Teams,
  Team,
  TeamMembers,
  TeamWorkspaces,
  WorkspaceList,
  DocumentSpaces,
  DocumentSpace,
  Profile,
  Dashboard,
  AnalyticsOverview,
  DocumentSpaceAnalytics,
  DocumentAnalytics,
  UsageAnalytics,
  PresentationAnalyticsSelector,
  PresentationAnalyticsDetail,
  MagicPagesStudio,
  DocumentEditor,
  DocumentProperties,
  DocumentPresentationAnalytics,
  StackedProjector,
  WorkspaceDetail,
  CustomFlows,
  CustomFlow,
  ViewCustomFlowPageByPage,
  CRMContacts,
  TemplateEditor,
  Connect,
  ProjectorCast,
  ScreenSession,
  DocumentHistory,
  Messages,
} from "@/views";
import AppAuth from "@/AppAuth.vue";
import AppPublic from "@/AppPublic.vue";
import { user, userData, getCurrentUser, notifyWhenUserReady } from "@/init";
import { watch } from "vue";
import {
  routeRequiresSharedSpaces,
  sharedSpacesDisabledRedirect,
} from "./sharedSpaces";
import { store } from "@/store";
import {
  Workspace,
  MyWorkspacesDocument,
  GetDocumentByIdDocument,
} from "@/graphql/operations";
import graphqlClient from "@/graphql/client";
import { messagesRouteMeta } from "@/views/messages/messagesAccess";
import { classicThemeRouteMeta } from "./classicTheme";
import {
  legacyModernRoutes,
  LEGACY_MODERN_PRESENTER,
  legacyDocumentTarget,
} from "./legacyModernRoutes";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/space/:path*",
    // The /2/... target lives outside the router, so this triggers a true page
    // load. Cancelling the in-router navigation (return false) keeps the
    // catch-all from flashing the 404 page while the browser fetches /2/...;
    // location.replace drops the bounce page from history so Back does not
    // re-trigger the redirect.
    component: { render: () => null },
    beforeEnter: (to) => {
      const href = to.fullPath.replace("/space", "/2");
      window.location.replace(href === "/2" ? "/2/" : href);
      return false;
    },
  },
  {
    path: "/",
    redirect: "/login",
    name: "AppPublic",
    component: AppPublic,
    children: [
      {
        path: "login",
        name: "Login",
        component: Login,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          hasHeader: true,
        },
      },
      {
        path: "verification",
        name: "EmailVerification",
        component: EmailVerification,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          hasHeader: true,
        },
      },
      {
        path: "connect",
        name: "Connect",
        component: Connect,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          hasHeader: false,
          hideBanners: true,
        },
      },
      {
        path: "presentation/:presentationId",
        name: "ProjectorCast",
        component: ProjectorCast,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          hasHeader: false,
          hideBanners: true,
        },
      },
      {
        path: "screensession/:id?",
        name: "ScreenSession",
        component: ScreenSession,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          hasHeader: false,
          hideBanners: true,
        },
      },
    ],
  },
  {
    /* auth required */
    path: "/u",
    name: "AppAuth",
    component: AppAuth,
    meta: {
      requiresAuth: true,
      requiresWorkspace: true,
    },
    children: [
      {
        path: "hcp-uploads/:hcpWorkspaceId?",
        name: "HcpInbox",
        component: () => import("@/hcp/HcpInboxView.vue"),
        props: (route) => ({
          workspaceId: route.params.hcpWorkspaceId || undefined,
          initialFileId: route.query.file || undefined,
        }),
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "workspaces",
        name: "WorkspaceList",
        component: WorkspaceList,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: ":workspaceId/detail",
        name: "WorkspaceDetail",
        component: WorkspaceDetail,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: true,
          displayLauncher: true,
          routeUpdateParam: "workspaceId",
        },
      },
      {
        path: ":workspaceId",
        name: "Workspace",
        component: WorkspaceOverview,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: true,
          displayLauncher: true,
          displayUploader: true,
          routeUpdateParam: "workspaceId",
          saveScrollPosition: true,
        },
      },
      {
        path: ":workspaceId/documents/:documentId",
        name: "DocumentEditor",
        component: DocumentEditor,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: true,
          displayLauncher: false,
          routeUpdateParam: "workspaceId",
        },
        children: [
          {
            path: "",
            name: "DocumentProperties",
            component: DocumentProperties,
          },
          {
            path: "magic_pages",
            name: "MagicPagesStudio",
            component: MagicPagesStudio,
          },
          {
            path: "analytics",
            name: "DocumentPresentationAnalytics",
            component: DocumentPresentationAnalytics,
            meta: {
              ...classicThemeRouteMeta,
              requiredFlag: "hasPresentationAnalytics",
            },
          },
        ],
      },
      {
        path: ":workspaceId/stackedprojector",
        name: "StackedProjector",
        component: StackedProjector,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: true,
          requiresAuth: true,
          displayLauncher: true,
          displayShare: true,
          routeUpdateParam: "workspaceId",
        },
      },
      {
        path: "analytics/presentation_history",
        name: "DocumentHistory",
        component: DocumentHistory,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresAuth: true,
          displayLauncher: false,
          requiredFlag: "hasDocumentTracking",
        },
      },
      {
        path: "tickets",
        name: "Tickets",
        component: () => import("@/views/tickets/Tickets.vue"),
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "messages",
        name: "Messages",
        component: Messages,
        meta: { ...messagesRouteMeta, ...classicThemeRouteMeta },
      },
      {
        path: "settings",
        name: "SettingsOverview",
        component: SettingsOverview,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: false,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "teams",
        name: "TeamList",
        component: Teams,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "teams/:id",
        name: "TeamDetail",
        component: Team,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          routeUpdateParam: "id",
        },
        children: [
          {
            path: "",
            name: "TeamMembers",
            component: TeamMembers,
          },
          {
            path: "workspaces",
            name: "TeamWorkspaces",
            component: TeamWorkspaces,
          },
        ],
      },
      {
        path: "onboarding",
        name: "JoinableTeams",
        redirect: { name: "TeamList" },
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "shared_spaces",
        name: "DocumentSpaces",
        component: DocumentSpaces,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: false,
          saveScrollPosition: true,
        },
      },
      {
        path: "shared_spaces/:id",
        name: "DocumentSpace",
        component: DocumentSpace,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: false,
          displaySpaceAnalyticsLink: true,
          routeUpdateParam: "id",
        },
      },
      {
        path: "shared_spaces/:spaceId/custom_flows/:id/view",
        name: "ViewSharedSpaceCustomFlowPageByPage",
        component: ViewCustomFlowPageByPage,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: true,
          projectorLauncherAbovePresenterControls: true,
          routeUpdateParam: "id",
        },
      },
      {
        path: "templates/:id",
        name: "TemplateEditor",
        component: TemplateEditor,
        meta: {
          ...classicThemeRouteMeta,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
        },
      },
      {
        path: "profile",
        name: "Profile",
        component: Profile,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
        },
      },
      {
        path: "analytics",
        name: "AnalyticsOverview",
        component: AnalyticsOverview,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasAnalytics",
        },
      },
      {
        path: "analytics/shared_spaces",
        name: "Dashboard",
        component: Dashboard,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: false,
          requiredFlag: "hasAnalytics",
        },
      },
      {
        path: "analytics/shared_spaces/:id",
        name: "DocumentSpaceAnalytics",
        component: DocumentSpaceAnalytics,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: false,
          requiredFlag: "hasAnalytics",
          routeUpdateParam: "id",
        },
      },
      {
        path: "analytics/documents",
        name: "DocumentAnalytics",
        component: DocumentAnalytics,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          requiresSharedSpaces: true,
          displayLauncher: false,
          requiredFlag: "hasDocumentAnalytics",
        },
      },
      {
        path: "analytics/usage",
        name: "UsageAnalytics",
        component: UsageAnalytics,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasUsageAnalytics",
        },
      },
      {
        path: "analytics/presentation_analytics",
        name: "PresentationAnalyticsSelector",
        component: PresentationAnalyticsSelector,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasPresentationAnalytics",
        },
      },
      {
        path: "analytics/presentation_analytics/:documentId",
        name: "PresentationAnalyticsDetail",
        component: PresentationAnalyticsDetail,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasPresentationAnalytics",
          routeUpdateParam: "documentId",
        },
      },
      {
        path: "custom_flows",
        name: "CustomFlows",
        component: CustomFlows,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasCustomFlows",
          saveScrollPosition: true,
        },
      },
      {
        path: "custom_flows/:id",
        name: "CustomFlow",
        component: CustomFlow,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: true,
          requiredFlag: "hasCustomFlows",
          routeUpdateParam: "id",
        },
      },
      {
        path: "custom_flows/:id/view",
        name: "ViewCustomFlowPageByPage",
        component: ViewCustomFlowPageByPage,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: true,
          requiredFlag: "hasCustomFlows",
          routeUpdateParam: "id",
        },
      },
      {
        path: "crm",
        name: "CRMContacts",
        component: CRMContacts,
        meta: {
          ...classicThemeRouteMeta,
          hasNavbar: true,
          requiresWorkspace: false,
          displayLauncher: false,
          requiredFlag: "hasSignConsent",
        },
      },
    ],
  },
  ...legacyModernRoutes,
  {
    path: "/404",
    name: "PageNotFound",
    component: PageNotFound,
    meta: {
      ...classicThemeRouteMeta,
      hasNavbar: false,
    },
  },
  {
    path: "/:catchAll(.*)",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

async function getRedirectRoute(currentRoute: RouteLocationNormalized) {
  const isAuthRoute = currentRoute.matched.some(
    (record) => record.meta.requiresAuth === true,
  );
  const isPublicRoute = currentRoute.path.startsWith("/") && !isAuthRoute;

  //immediatly check routes for which the user is not needed -- e.g. connect
  if (
    isPublicRoute &&
    currentRoute.name !== "Login" &&
    currentRoute.name !== "EmailVerification"
  ) {
    return null;
  }

  // fetch user
  let currentUser = user.value;
  if (!currentUser) {
    currentUser = await getCurrentUser();
  }

  // no user
  if (!currentUser) {
    if (isPublicRoute) {
      return null;
    } else {
      console.log("redirect to login");
      //redirect to login
      return "/login";
    }
  }

  if (!currentUser.emailVerified) {
    console.log("redirect to verfication");
    if (currentRoute.name === "EmailVerification") {
      return null;
    } else {
      return "/verification";
    }
  }

  let requiresWorkspace = currentRoute.meta.requiresWorkspace;
  const requiredFlag: string | undefined = currentRoute.meta.requiredFlag
    ? (currentRoute.meta.requiredFlag as string)
    : undefined;
  const requiresSharedSpaces = routeRequiresSharedSpaces(currentRoute);

  // logged in and verified users should not see Login or Verification page
  if (
    currentRoute.name === "Login" ||
    (currentRoute.name === "EmailVerification" && currentUser.emailVerified)
  ) {
    requiresWorkspace = true;
  }

  await notifyWhenUserReady();

  if (currentRoute.name === LEGACY_MODERN_PRESENTER) {
    const documentId = currentRoute.params.documentId;
    if (typeof documentId !== "string" || !/^[0-9]+$/.test(documentId))
      return { name: "PageNotFound", replace: true };
    try {
      // Use the authenticated, permission-filtered document query. Modern URLs
      // contain a Postgres document ID; Classic needs its workspace + Firestore ID.
      const result = await graphqlClient
        .query(
          GetDocumentByIdDocument,
          { id: documentId },
          { requestPolicy: "network-only" },
        )
        .toPromise();
      return legacyDocumentTarget(
        result.error ? undefined : result.data?.document,
        currentRoute,
      );
    } catch {
      return { name: "PageNotFound", replace: true };
    }
  }

  // Existing workspace/projector deep links open the same managed reading view.
  let linkedWorkspace = store.getters.workspaces.find(
    (workspace: Workspace) => workspace.id === currentRoute.params.workspaceId,
  );
  if (currentRoute.params.workspaceId && !linkedWorkspace) {
    // A first upload or access grant can arrive before the next Init refresh.
    const current = await graphqlClient
      .query(MyWorkspacesDocument, {}, { requestPolicy: "network-only" })
      .toPromise();
    linkedWorkspace = current.data?.workspaces?.nodes.find(
      (workspace: Workspace | null) =>
        workspace?.id === currentRoute.params.workspaceId,
    );
    if (!linkedWorkspace) return "/u/workspaces";
  }
  if (
    linkedWorkspace?.isHcpUploadWorkspace ||
    linkedWorkspace?.root?.isHcpUploadWorkspace
  ) {
    return {
      name: "HcpInbox",
      params: {
        hcpWorkspaceId:
          linkedWorkspace.root?.firebaseId ?? linkedWorkspace.firebaseId,
      },
      query: { file: currentRoute.query.fid ?? currentRoute.params.documentId },
    };
  }

  if (!requiredFlag && !requiresWorkspace && !requiresSharedSpaces) {
    return null;
  }

  store.commit("setCurrentWorkspace", { workspace: undefined });

  if (
    requiredFlag &&
    !store.getters.user[requiredFlag].apply(store.getters.user)
  ) {
    return "/404";
  }

  if (requiresSharedSpaces && !store.getters.user.canUseSharedSpaces()) {
    return sharedSpacesDisabledRedirect();
  }

  if (!requiresWorkspace) {
    return null;
  }

  if (currentRoute.params.workspaceId !== undefined) {
    const workspaceParam = currentRoute.params.workspaceId as string;

    if (
      store.getters.currentWorkspace === undefined ||
      store.getters.currentWorkspace.id !== workspaceParam
    ) {
      const currentWorkspace = linkedWorkspace;
      if (currentWorkspace) {
        store.commit("setCurrentWorkspace", { workspace: currentWorkspace });
      } else {
        store.commit("setCurrentWorkspace", { workspace: undefined });
      }
    }

    return null;
  }

  if (!userData.value) {
    return null;
  }

  ///* find a default workspace */
  if (store.getters.rootWorkspaces) {
    const defaultWorkspace = store.getters.rootWorkspaces[0];
    if (defaultWorkspace && defaultWorkspace.id) {
      store.commit("setCurrentWorkspace", { workspace: defaultWorkspace });
      return `/u/${defaultWorkspace.id}`;
    }
  }

  return "/u/workspaces";
}

let navigationVersion = 0;

async function route(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: Function,
) {
  const version = ++navigationVersion;
  const redirectRoute = await getRedirectRoute(to);
  if (version !== navigationVersion) {
    // Let Vue Router classify this as cancelled; aborting would roll back popstate.
    next();
    return;
  }
  if (redirectRoute !== null) {
    next(redirectRoute);
  } else {
    next();
  }
}

router.beforeEach((to, from, next) => {
  route(to, from, next);
});

watch(userData, async () => {
  const currentRoute = router.currentRoute.value;
  const redirectRoute = await getRedirectRoute(currentRoute);
  if (redirectRoute !== null && router.currentRoute.value === currentRoute) {
    router.replace(redirectRoute);
  }
});

export default router;
